import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Shield, TrendingUp, Users, ChevronRight, Building2, MapPin, Bed, Bath, SquareIcon, Star, Lock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/services/api';

const HERO_BG = 'https://images.unsplash.com/photo-1768069794857-9306ac167c6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMHNreWxpbmUlMjBuaWdodCUyMGx1eHVyeXxlbnwwfHx8fDE3NzA4MjIyNjF8MA&ixlib=rb-4.1.0&q=85';

const cities = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah'];

export const LandingPage = () => {
  const navigate = useNavigate();
  const [listingType, setListingType] = useState('buy');
  const [selectedCity, setSelectedCity] = useState('Dubai');
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await api.get('/properties/featured?limit=6');
        setFeaturedProperties(response.data);
      } catch (error) {
        console.error('Failed to fetch featured properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const formatPrice = (price, type) => {
    if (price >= 1000000) {
      return `AED ${(price / 1000000).toFixed(1)}M${type === 'rent' ? '/yr' : ''}`;
    }
    return `AED ${price.toLocaleString()}${type === 'rent' ? '/yr' : ''}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-2 cursor-pointer group">
              <div className="w-10 h-10 rounded logo-icon-metallic flex items-center justify-center">
                <span className="font-display text-xl text-primary-foreground font-bold">R</span>
              </div>
              <span className="font-display text-2xl font-medium tracking-tight gold-interactive">Rashii</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#properties" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300">Properties</a>
              <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300">How It Works</a>
              <a href="#about" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300">About</a>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                className="text-sm hover:text-primary transition-colors"
                onClick={() => navigate('/login')}
                data-testid="nav-login-btn"
              >
                Log In
              </Button>
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-6"
                onClick={() => navigate('/register')}
                data-testid="nav-get-started-btn"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_BG} 
            alt="Dubai Skyline" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
        </div>
        
        {/* Gold Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5">
                <Shield className="w-4 h-4 text-primary" />
                <span className="text-xs uppercase tracking-widest text-primary">Verified & Exclusive</span>
              </div>
              
              <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none animate-fade-in-up">
                The Thinking Engine for{' '}
                <span className="gold-interactive">Dubai Real Estate</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl animate-fade-in-up stagger-1">
                Not just listings. Investment-grade intelligence. Know what to buy, when to buy, and why it makes sense.
              </p>
              
              {/* Search Box */}
              <div className="glass-card p-6 rounded-sm space-y-4 animate-fade-in-up stagger-2">
                <Tabs defaultValue="buy" onValueChange={setListingType}>
                  <TabsList className="bg-secondary/50">
                    <TabsTrigger value="buy" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest text-xs">
                      Buy
                    </TabsTrigger>
                    <TabsTrigger value="rent" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase tracking-widest text-xs">
                      Rent
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input 
                      placeholder="Search location..." 
                      className="pl-10 bg-transparent border-white/20 focus:border-primary h-12"
                      data-testid="search-location-input"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          navigate(`/properties?q=${encodeURIComponent(e.target.value)}&type=${listingType}`);
                        }
                      }}
                    />
                  </div>
                  <Button 
                    className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs font-bold"
                    onClick={() => {
                      const searchInput = document.querySelector('[data-testid="search-location-input"]');
                      const query = searchInput?.value || '';
                      navigate(`/properties?q=${encodeURIComponent(query)}&type=${listingType}`);
                    }}
                    data-testid="search-btn"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>
              </div>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 pt-4 animate-fade-in-up stagger-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground">KYC Verified Buyers</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground">Data Protected</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground">Exclusive Listings</span>
                </div>
              </div>
            </div>
            
            {/* Stats Card */}
            <div className="lg:col-span-5">
              <Card className="glass-card border-white/10 gold-glow">
                <CardContent className="p-8 space-y-6">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Platform Stats</div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-4xl font-display font-medium text-primary">250+</div>
                      <div className="text-sm text-muted-foreground">Verified Properties</div>
                    </div>
                    <div>
                      <div className="text-4xl font-display font-medium text-primary">AED 2.5B</div>
                      <div className="text-sm text-muted-foreground">Total Value</div>
                    </div>
                    <div>
                      <div className="text-4xl font-display font-medium text-primary">98%</div>
                      <div className="text-sm text-muted-foreground">Verification Rate</div>
                    </div>
                    <div>
                      <div className="text-4xl font-display font-medium text-primary">9</div>
                      <div className="text-sm text-muted-foreground">Max Viewings to Sale</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 md:py-32 relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-primary">The Rashii Process</span>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 gold-interactive">Assess, Score, Decide</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Verify Identity', desc: 'Complete KYC and proof of funds. Your data is verified but never stored.' },
              { icon: TrendingUp, title: 'Get Rashii Score', desc: 'AI-powered investment analysis with ROI, yield, and liquidity metrics.' },
              { icon: Users, title: 'Direct Negotiation', desc: 'Connect directly with verified sellers. Max 3 properties to view, max 9 viewings to sale.' }
            ].map((item, idx) => (
              <Card key={idx} className="glass-card border-white/10 hover:border-primary/50 transition-all duration-500 group">
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded logo-icon-metallic flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <item.icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h3 className="font-display text-xl mb-3 gold-interactive">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section id="properties" className="py-24 md:py-32 relative">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-primary">Curated Selection</span>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4">Featured Properties</h2>
            </div>
            <Button 
              variant="outline" 
              className="btn-gold-metallic-outline mt-4 md:mt-0"
              onClick={() => navigate('/properties')}
            >
              View All <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          
          {/* City Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {cities.map(city => (
              <Button
                key={city}
                variant={selectedCity === city ? 'default' : 'outline'}
                size="sm"
                className={selectedCity === city ? 'bg-primary text-primary-foreground' : 'border-border hover:border-primary'}
                onClick={() => setSelectedCity(city)}
              >
                {city}
              </Button>
            ))}
          </div>
          
          {/* Properties Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-properties-grid">
            {loading ? (
              Array(6).fill(0).map((_, idx) => (
                <Card key={idx} className="glass-card animate-pulse">
                  <div className="h-48 bg-secondary/50" />
                  <CardContent className="p-6 space-y-4">
                    <div className="h-4 bg-secondary/50 rounded w-3/4" />
                    <div className="h-3 bg-secondary/50 rounded w-1/2" />
                  </CardContent>
                </Card>
              ))
            ) : featuredProperties.length > 0 ? (
              featuredProperties.map((property) => (
                <Card 
                  key={property.id} 
                  className="glass-card border-white/10 hover:border-primary/50 transition-all duration-500 overflow-hidden group cursor-pointer"
                  onClick={() => navigate(`/property/${property.id}`)}
                  data-testid={`property-card-${property.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={property.images?.[0] || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'} 
                      alt={property.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <Badge className="bg-primary text-primary-foreground uppercase text-[10px]">
                        {property.listing_type === 'buy' ? 'For Sale' : 'For Rent'}
                      </Badge>
                      {property.verification_status === 'verified' && (
                        <Badge className="bg-green-600 text-white uppercase text-[10px]">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Verified
                        </Badge>
                      )}
                    </div>
                    {property.rashii_score && (
                      <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-sm px-3 py-1 rounded">
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-primary fill-primary" />
                          <span className="text-sm font-bold text-primary">{property.rashii_score.overall_score}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                      <MapPin className="w-3 h-3" />
                      {property.location}
                    </div>
                    <h3 className="font-display text-lg mb-2 group-hover:text-primary transition-colors">
                      {property.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <span className="flex items-center gap-1"><Bed className="w-4 h-4" /> {property.bedrooms}</span>
                      <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> {property.bathrooms}</span>
                      <span className="flex items-center gap-1"><SquareIcon className="w-4 h-4" /> {property.area_sqft.toLocaleString()} sqft</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-display text-primary">
                        {formatPrice(property.price, property.listing_type)}
                      </span>
                      <Button size="sm" variant="outline" className="border-primary/30 hover:bg-primary hover:text-primary-foreground text-xs">
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-3 text-center py-12">
                <Building2 className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground">Properties coming soon</p>
                <p className="text-sm text-muted-foreground/70 mt-2">Verified listings will appear here once approved</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
        
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <div className="glass-card p-12 md:p-16 rounded-sm text-center gold-glow">
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mb-6">
              Ready to Make Smarter Decisions?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Join verified buyers and sellers on the platform that prioritizes intelligence over listings.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-6"
                onClick={() => navigate('/register')}
                data-testid="cta-get-started-btn"
              >
                Start Your Assessment
              </Button>
              <Button 
                variant="outline" 
                className="border-gold-400/50 text-foreground hover:border-gold-400 hover:bg-gold-600/10 hover:text-gold-400 uppercase tracking-widest text-xs px-8 py-6"
                onClick={() => navigate('/login')}
              >
                I Have an Account
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 bg-secondary/20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 cursor-pointer group mb-4" onClick={() => navigate('/')}>
                <div className="w-10 h-10 rounded logo-icon-metallic flex items-center justify-center">
                  <span className="font-display text-lg text-primary-foreground font-bold">R</span>
                </div>
                <span className="font-display text-xl font-medium tracking-tight gold-interactive">Rashii</span>
              </div>
              <p className="text-sm text-muted-foreground max-w-md">
                Decision Intelligence for Real Estate. The verified exchange platform where smart buyers meet serious sellers.
              </p>
            </div>
            
            {/* Legal Links */}
            <div>
              <h4 className="font-display text-sm font-medium mb-4 text-foreground">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <a href="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/cookies" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    Cookie Policy
                  </a>
                </li>
              </ul>
            </div>
            
            {/* Contact & Social */}
            <div>
              <h4 className="font-display text-sm font-medium mb-4 text-foreground">Contact</h4>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:support@rashii.ae" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    support@rashii.ae
                  </a>
                </li>
                <li>
                  <a href="tel:+97142345678" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    +971 4 234 5678
                  </a>
                </li>
                <li className="pt-2">
                  <div className="flex items-center gap-4">
                    <a href="https://instagram.com/rashii.ae" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    </a>
                    <a href="https://linkedin.com/company/rashii" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                    </a>
                    <a href="https://x.com/rashii_ae" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </a>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-xs text-muted-foreground">
                © 2024 Rashii. All rights reserved. Dubai, UAE.
              </p>
              <p className="text-xs text-muted-foreground">
                RERA Licensed | DLD Registered
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
